import useOutsideClick from 'hooks/useOutsideClick';
import * as React from 'react';
import { isLang } from 'services';
import styled, { css, keyframes } from 'styled-components';
import { AllPopupOverlay } from 'style/CommonStyledComponents';
import { openChatBot, ssqutil } from 'util/ssqutil';
import { BRKS } from 'util/variables';
import { MainMenuEn } from './MainMenuEn';
import { MainMenuKr } from './MainMenuKr';
import { MainMenuUs } from './MainMenuUs';
import useMenuUrl from './useMenuUrl';

type Props = {
  onClose: () => void;
  top?: number;
  main: boolean;
  onClickAiChatbot: () => void;
  belowHeader?: boolean; // 모바일: 헤더 바로 아래에 flush로 고정
  headerRef?: React.RefObject<HTMLDivElement>; // 모바일: 헤더 내부 클릭은 외부클릭(닫힘)에서 제외
};
export const MainMenu = (props: Props) => {
  const { onClose, top = 0, main, onClickAiChatbot, belowHeader = false, headerRef } = props;
  const ref = React.useRef<HTMLDivElement>(null);

  // 모바일(belowHeader)에선 헤더 내부 클릭으로 메뉴가 닫히지 않도록 헤더 ref도 예외 대상에 포함한다.
  // (오버레이는 DOM상 헤더 하위라 이 예외에 포함되므로, 닫힘은 Overlay의 onClick으로 별도 처리한다.)
  const outsideRefs = React.useMemo(
    () => (belowHeader && headerRef ? [ref, headerRef] : [ref]),
    [belowHeader, headerRef]
  );

  // 모바일(belowHeader): 메뉴/오버레이의 상단 오프셋을 고정값(헤더 높이)이 아니라 헤더의 "실제" 현재 하단 위치에 맞춘다.
  // dlcenter 등 scroll-header 페이지에서는 스크롤 시 헤더가 뷰포트 위로 밀려 올라가므로, 스크롤 도중 메뉴를 열면
  // 고정 오프셋과 실제 헤더 하단이 어긋나 상단에 틈이 생긴다. 여는 순간 헤더 하단을 측정해 그 지점에 flush로 붙인다.
  // (메뉴가 열리면 body 스크롤이 잠겨 헤더 위치가 고정되므로 1회 측정으로 충분하며, 방향 전환 등 리사이즈만 재측정한다.)
  // 헤더가 완전히 밀려 사라진 경우 0으로 클램프되어 메뉴가 화면 전체를 덮는다. 측정 불가 시 null → CSS 기본값(헤더 높이)로 폴백.
  const [headerBottomPx, setHeaderBottomPx] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    if (!belowHeader) return;
    const measure = () => {
      const el = headerRef?.current;
      setHeaderBottomPx(el ? Math.max(0, el.getBoundingClientRect().bottom) : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [belowHeader, headerRef]);

  useOutsideClick(
    outsideRefs,
    () => {
      onClose();
    },
    [belowHeader]
  );

  // 모바일(belowHeader): 메뉴가 열려 있는 동안 뒷배경(body) 스크롤을 잠근다.
  // MainMenu는 메뉴가 열릴 때만 마운트되므로 마운트=열림/언마운트=닫힘에 맞춰 복원한다.
  React.useEffect(() => {
    if (!belowHeader) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [belowHeader]);

  return (
    <>
      {/* 모바일: 헤더/메뉴 이외 영역을 덮어 조작을 차단하는 오버레이(클릭 시 닫힘).
          ssq-mainmenu-overlay: 모바일 메뉴 열림의 DOM 마커 — GlobalStyle이 :has()로 감지해
          우하단 AI 챗봇 런처를 숨긴다. */}
      {belowHeader && <Overlay className="ssq-mainmenu-overlay" onClick={onClose} $topPx={headerBottomPx} />}
      <Body ref={ref} $top={top} $main={main} $belowHeader={belowHeader} $topPx={headerBottomPx}>
        {isLang('ko-KR') && <MainMenuKr onClose={onClose} onClickAiChatbot={onClickAiChatbot} />}
        {isLang('en-US') && <MainMenuUs onClose={onClose} onClickAiChatbot={onClickAiChatbot}  />}
        {isLang('en') && <MainMenuEn onClose={onClose} onClickAiChatbot={onClickAiChatbot} />}
      </Body>
    </>
  );
};

// 모바일: 메뉴가 오른쪽 화면 밖에서 안으로 슬라이드되어 들어온다.
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

// 모바일: 헤더 아래 전체 영역을 덮는 딤 오버레이. 공통 AllPopupOverlay를 상속하되
// 헤더는 가리지 않도록(top 오프셋) 메뉴보다는 아래로(z-index) 위치만 재정의한다.
// top은 헤더의 실제 현재 하단($topPx)에 맞춘다(스크롤로 헤더가 밀려 올라가도 틈 없이 붙도록). 측정 불가 시 헤더 높이로 폴백.
const Overlay = styled(AllPopupOverlay)<{ $topPx: number | null }>`
  top: ${(p) => (p.$topPx != null ? `${p.$topPx}px` : '9.5rem')};
  height: auto;
  bottom: 0;
  z-index: 100;
`;

const Body = styled.div<{ $top: number; $main: boolean; $belowHeader: boolean; $topPx: number | null }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: ${(p) => p.$top}rem;
  right: ${(p) => (p.$main ? '-3rem' : '-3rem')};
  width: 30rem;
  background-color: white;
  color: black;
  border-radius: 0.8rem;
  /* 하단 패딩은 각 메뉴의 BottomSpace로 대체(바닥 하위메뉴 펼침 시 제거되도록). 상단만 유지. */
  padding: 1rem 0 0;
  max-height: calc(100vh - 11rem);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  box-shadow: 0 0.4rem 1.6rem rgba(0, 0, 0, 0.1);
  @media (max-width: ${BRKS.tabletMax}) {
    right: ${(p) => (p.$main ? '-1.5rem' : '-3rem')};
  }

  /* 헤더 하단 부착(모바일): 흰색 Header 바로 아래에 flush. top은 헤더의 실제 현재 하단($topPx)에 맞춘다
     (스크롤로 헤더가 밀려 올라가도 틈 없이 붙고, 완전히 사라지면 top:0으로 화면 전체를 채운다). 측정 불가 시 헤더 높이로 폴백.
     bottom:0 + max-height:none으로 패널을 화면 하단까지 꽉 채운다(base의 데스크톱용 max-height 캡 해제).
     메뉴가 부족하면 흰 배경(흰색 Body + 하단 flex:1)으로 채워지고, 넘치면 세로 스크롤된다. */
  ${(p) =>
    p.$belowHeader &&
    css`
      position: fixed;
      top: ${p.$topPx != null ? `${p.$topPx}px` : '9.5rem'};
      right: 0;
      bottom: 0;
      max-height: none;
      width: min(35.5rem, 77vw);
      z-index: 101;
      border-radius: 0;
      box-shadow: none;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      animation: ${slideInFromRight} 0.3s ease-out;
      @media (max-width: ${BRKS.tabletMax}) {
        right: 0;
      }
    `}
`;
